import {
  applyNodeChanges,
  type NodeChange,
} from '@xyflow/react';
import type { Position } from '@/types/workflowDefinition';
import type { GraphNode } from '@/hooks/useWorkflowGraph';
import { calculateScopeContainerBound } from '@/utils/workflow';

export type PhantomPositionUpdates = ReadonlyMap<string, Position>;

export const applyDesignerNodeChanges = (
  nodes: GraphNode[],
  changes: NodeChange<GraphNode>[],
  phantomPositions: PhantomPositionUpdates,
  affectedScopeContainerIds: ReadonlySet<string>,
): GraphNode[] => {
  const changedNodes = changes.length > 0
    ? applyNodeChanges(changes, nodes)
    : nodes;
  if (
    phantomPositions.size === 0
    && affectedScopeContainerIds.size === 0
  ) {
    return changedNodes;
  }

  const nodeById = new Map<string, GraphNode>();
  const nodeIndexById = new Map<string, number>();
  changedNodes.forEach((node, index) => {
    nodeById.set(node.id, node);
    nodeIndexById.set(node.id, index);
  });

  let result: GraphNode[] | undefined;
  const replaceNode = (nodeId: string, replacement: GraphNode): void => {
    const index = nodeIndexById.get(nodeId);
    if (index === undefined) return;
    result ??= [...changedNodes];
    result[index] = replacement;
    nodeById.set(nodeId, replacement);
  };

  for (const [nodeId, position] of phantomPositions) {
    const node = nodeById.get(nodeId);
    if (
      !node
      || (
        node.position.x === position.x
        && node.position.y === position.y
      )
    ) {
      continue;
    }
    replaceNode(nodeId, { ...node, position });
  }

  for (const scopeId of affectedScopeContainerIds) {
    const node = nodeById.get(scopeId);
    if (!node) continue;
    const bounds = calculateScopeContainerBound(node, nodeById);
    if (
      !bounds
      || (
        node.position.x === bounds.position.x
        && node.position.y === bounds.position.y
        && node.data.containerWidth === bounds.width
        && node.data.containerHeight === bounds.height
      )
    ) {
      continue;
    }
    replaceNode(scopeId, {
      ...node,
      position: bounds.position,
      data: {
        ...node.data,
        containerWidth: bounds.width,
        containerHeight: bounds.height,
      },
    });
  }

  return result ?? changedNodes;
};
